import React, {
  FC,
  FocusEvent,
  useCallback,
  ChangeEvent,
  PropsWithChildren,
} from "react";

import classNames from "classnames";

import {
  StyledMenuSelector,
  StyledMenuSelectorBack,
  StyledMenuSelectorDots,
  StyledMenuSelectorInner,
} from "./Styles";
import { Input } from "../Input";

interface IMenuSelector {
  /** Initial value on mount */
  defaultValue?: string;
  /** Input placeholder */
  placeholder?: string;
  /** Execute callback when input has been changed */
  onChange?: (value: string) => void;
  /** Execute callback when click on back button */
  onBack?: () => void;
  /** Execute callback when click on edit button */
  onEdit?: () => void;
  /** Auto focus on mount */
  autoFocus?: boolean;
  /** Applies passed classes */
  className?: string;
  /** Input focus event callback */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /** Input focus event callback */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** Set auto complete option on/off */
  autoComplete?: boolean;
}

export const MenuSelector: FC<PropsWithChildren<IMenuSelector>> = ({
  defaultValue,
  children,
  placeholder,
  onChange,
  onBack,
  onEdit,
  autoFocus = false,
  className,
  onFocus,
  onBlur,
  autoComplete = false,
}) => {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (typeof onChange === "function") {
        onChange(e.target.value);
      }
    },
    [onChange]
  );

  return (
    <StyledMenuSelector className={classNames("c-menu-selector", className)}>
      <StyledMenuSelectorInner>
        {typeof onBack === "function" ? (
          <StyledMenuSelectorBack onClick={onBack} width="16px" height="16px" />
        ) : null}
        <Input
          onChange={handleChange}
          placeholder={placeholder}
          autoFocus={autoFocus}
          defaultValue={defaultValue}
          onFocus={onFocus}
          onBlur={onBlur}
          autoComplete={autoComplete ? "on" : "off"}
        />
        {typeof onEdit === "function" ? (
          <StyledMenuSelectorDots onClick={onEdit} />
        ) : null}
      </StyledMenuSelectorInner>
      {children}
    </StyledMenuSelector>
  );
};

MenuSelector.displayName = "MenuSelector";
